import { useCallback, useEffect, useState } from 'react';
import { logInfo } from 'setup/logger/logInfo';

export const PROJECT_PICKER_MAX_WAIT_MS = 45000;

export type ProjectPickerSyncStatus =
  | 'loading'
  | 'ready'
  | 'offline'
  | 'syncFailed';

interface ProjectPickerSyncInput {
  hasProjects: boolean;
  projectsLoading: boolean;
  projectsSyncFailed: boolean;
  hasOrganizations: boolean;
  organizationsLoading: boolean;
  isOffline: boolean;
  maxWaitMs?: number;
}

export function useProjectPickerSyncState({
  hasProjects,
  projectsLoading,
  projectsSyncFailed,
  hasOrganizations,
  organizationsLoading,
  isOffline,
  maxWaitMs = PROJECT_PICKER_MAX_WAIT_MS,
}: ProjectPickerSyncInput): {
  status: ProjectPickerSyncStatus;
  restartMaxWait: () => void;
} {
  const waiting =
    (!hasProjects && projectsLoading) ||
    (!hasOrganizations && organizationsLoading);
  const [maxWaitReached, setMaxWaitReached] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const restartMaxWait = useCallback((): void => {
    setMaxWaitReached(false);
    setAttempt((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!waiting) {
      setMaxWaitReached(false);
      return;
    }
    const timeout = setTimeout(() => {
      logInfo('project-picker-max-wait-reached', { maxWaitMs });
      setMaxWaitReached(true);
    }, maxWaitMs);

    return (): void => {
      clearTimeout(timeout);
    };
  }, [waiting, maxWaitMs, attempt]);

  return {
    status: getStatus({
      hasProjects,
      waiting,
      maxWaitReached,
      projectsSyncFailed,
      isOffline,
    }),
    restartMaxWait,
  };
}

function getStatus({
  hasProjects,
  waiting,
  maxWaitReached,
  projectsSyncFailed,
  isOffline,
}: {
  hasProjects: boolean;
  waiting: boolean;
  maxWaitReached: boolean;
  projectsSyncFailed: boolean;
  isOffline: boolean;
}): ProjectPickerSyncStatus {
  if (isOffline && !hasProjects) {
    return 'offline';
  }
  if (waiting && !maxWaitReached) {
    return 'loading';
  }
  if (!hasProjects && (projectsSyncFailed || maxWaitReached)) {
    return 'syncFailed';
  }
  return 'ready';
}
